'use client'

import { createContext, useContext, type ReactNode } from 'react'
import { useSubscriptionInfo, type SubscriptionStatus } from './use-subscription'

export interface SubscriptionContextValue {
  status: SubscriptionStatus
  stripeCustomerId: string | null
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null)

/**
 * Wraps children with a single shared subscription query.
 * Place inside (gated)/layout.tsx so all course pages share one polling instance.
 */
export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { status, stripeCustomerId } = useSubscriptionInfo()
  return (
    <SubscriptionContext.Provider value={{ status, stripeCustomerId }}>
      {children}
    </SubscriptionContext.Provider>
  )
}

/**
 * Provides inactive subscription state for shells that don't use paid subscription (e.g. free VHF).
 * Use so components like VhfSidebarAccountMenu can call useSubscriptionContext without throwing.
 */
export function SubscriptionProviderInactive({ children }: { children: ReactNode }) {
  return (
    <SubscriptionContext.Provider value={{ status: 'none', stripeCustomerId: null }}>
      {children}
    </SubscriptionContext.Provider>
  )
}

/**
 * Reads shared subscription info inside the gated layout.
 * Throws if used outside a SubscriptionProvider.
 */
export function useSubscriptionContext(): SubscriptionContextValue {
  const ctx = useContext(SubscriptionContext)
  if (!ctx) throw new Error('useSubscriptionContext must be used inside SubscriptionProvider')
  return ctx
}
